'use client';
import React from 'react';
import { CheckoutForm as Shared } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { convertToLocale } from '@lib/util/money';

const SharedRender = (Shared as any).render;

/**
 * Storefront CheckoutForm — multi-step (contact → shipping → payment) form
 * that:
 *   1. Reads shipping + payment methods from Puck context
 *   2. Submits the chosen address/shipping/payment to Medusa via /api/cart/*
 *   3. POSTs to /api/cart/complete to place the order
 *   4. Redirects to /[countryCode]/order/:id/confirmed on success
 *
 * Falls back to the shared MOCK (DEFAULT_SHIPPING + DEFAULT_PAYMENT) in
 * editor preview.
 */

interface CheckoutFormData {
  email: string;
  firstName: string;
  lastName: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone?: string;
  shippingMethod: string;
  paymentMethod: string;
  orderNotes?: string;
}

function CheckoutFormImpl(props: any) {
  const { context } = usePuckContext() as any;
  const cart = context?.cart;
  const countryCode: string = context?.countryCode || 'us';
  const availableShipping = context?.availableShippingMethods;
  const availablePayment = context?.availablePaymentMethods;

  // Build the prop shapes the shared component expects.
  // ShippingMethod: { id, name, price: number (cents), estimate }
  // PaymentMethod: { id, name, icon }
  const shippingMethods = (availableShipping ?? []).map((o: any) => ({
    id: o.id,
    name: o.name ?? o.provider?.id ?? 'Shipping',
    price: Number(o.amount) || 0,
    estimate: o.data?.eta_days
      ? `${o.data.eta_days} business day${o.data.eta_days === 1 ? '' : 's'}`
      : (o.price_type === 'calculated' ? 'Calculated at checkout' : 'Standard delivery'),
  }));

  const ICONS: Record<string, string> = {
    pp_stripe_stripe: '💳',
    pp_paypal_paypal: '🅿️',
    pp_apple_pay_apple: '🍎',
    pp_google_pay_google: '🇬',
    pp_system_default: '💵',
  };
  const paymentMethods = (availablePayment ?? []).map((p: any) => {
    const id: string = p.id;
    const nice = id.replace(/^pp_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return {
      id,
      name: nice,
      icon: ICONS[id] ?? '💳',
    };
  });

  const currency = cart?.currency_code || 'usd';
  const formatPrice = (price: number) =>
    convertToLocale({ amount: Number(price) || 0, currency_code: currency });

  // On submit: ship address + method + payment → complete order → redirect.
  const onSubmit = async (data: CheckoutFormData) => {
    // 1. Shipping address
    const addrRes = await fetch('/api/cart/address', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: data.email,
        shippingAddress: {
          first_name: data.firstName,
          last_name: data.lastName,
          address_1: data.address,
          address_2: data.address2,
          city: data.city,
          province: data.state,
          postal_code: data.zipCode,
          country_code: data.country?.toLowerCase(),
          phone: data.phone,
        },
      }),
    });
    if (!addrRes.ok) {
      const err = await addrRes.json().catch(() => ({}));
      throw new Error(err?.error || 'Failed to save address');
    }

    // 2. Shipping method
    const shipRes = await fetch('/api/cart/shipping-method', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shippingMethodId: data.shippingMethod }),
    });
    if (!shipRes.ok) {
      const err = await shipRes.json().catch(() => ({}));
      throw new Error(err?.error || 'Failed to set shipping method');
    }

    // 3. Payment method (initialize the payment session)
    const payRes = await fetch('/api/cart/payment-method', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ providerId: data.paymentMethod }),
    });
    if (!payRes.ok) {
      const err = await payRes.json().catch(() => ({}));
      throw new Error(err?.error || 'Failed to set payment method');
    }

    // 4. Complete the cart → places the order
    const completeRes = await fetch('/api/cart/complete', { method: 'POST' });
    if (!completeRes.ok) {
      const err = await completeRes.json().catch(() => ({}));
      throw new Error(err?.error || 'Failed to place order');
    }
    const completeData = await completeRes.json();
    const orderId: string | undefined = completeData?.orderId;
    if (!orderId) throw new Error('Order placed but no orderId returned');

    // 5. Redirect to the success page
    window.location.href = `/${countryCode}/order/${orderId}/confirmed`;
  };

  // Pre-fill the email from the cart if present.
  const initialStep = (cart?.email ? 1 : 0) as 0 | 1;

  return (
    <SharedRender
      {...props}
      shippingMethods={shippingMethods}
      paymentMethods={paymentMethods}
      onSubmit={onSubmit}
      formatPrice={formatPrice}
      initialStep={initialStep}
    />
  );
}

export const CheckoutForm: any = {
  ...Shared,
  render: (props: any) => <CheckoutFormImpl {...props} />,
};
export default CheckoutForm;
